import { FocusEvent, useMemo, useState } from 'react';
import { combineClassNames, InputNumber, InputNumberProps } from '@arkestro/arkestro-design-system';

import {
  CurrencyFormatOptions,
  currencyFormatter,
  currencyParser,
  getCurrencyFormatOptions,
} from './utils';
import style from './PriceInputField.module.scss';

type PriceInputFieldProps = InputNumberProps & {
  currency?: string;
  precision: number;
};

export const PriceInputField = ({ currency, precision, ...attrs }: PriceInputFieldProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const options: CurrencyFormatOptions | undefined = useMemo(() => (
    currency ? getCurrencyFormatOptions(currency, precision) : undefined
  ), [currency, precision]);

  if (!currency || !options) return <InputNumber data-testid='number-input' {...attrs} step={1} type='number' />;
  const { symbol, symbolPosition, decimalSeparator, step } = options;
  const handleBlur = (e: FocusEvent<HTMLInputElement>): void => {
    setIsFocused(false);
    if (attrs.onBlur) attrs.onBlur(e);
  };
  const handleFocus = (e: FocusEvent<HTMLInputElement>): void => {
    setIsFocused(true);
    if (attrs.onFocus) attrs.onFocus(e);
  };

  return (
    <InputNumber
      data-testid='price-input'
      {...attrs}
      addonAfter={symbolPosition === 'right' ? symbol : undefined}
      addonBefore={symbolPosition === 'left' ? symbol : undefined}
      className={combineClassNames(attrs.className || '', style.input_number)}
      decimalSeparator={decimalSeparator}
      formatter={isFocused ? undefined : currencyFormatter(options)} // disables formatting while typing
      onBlur={handleBlur}
      onFocus={handleFocus}
      parser={currencyParser(decimalSeparator)}
      step={step}
      type='currency'
    />
  );
};
